import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { db } from "@/lib/db-client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { eur, fmtDateTime } from "@/lib/format";
import { createInvoice } from "@/lib/admin-api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { FileText, ChevronDown, ChevronRight } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/")({
  component: PendingInvoices,
});

function PendingInvoices() {
  const [tickets, setTickets] = useState<any[]>([]);
  const [pricing, setPricing] = useState<Record<string, number>>({});
  const [settings, setSettings] = useState<{ backoffice_email: string }>({ backoffice_email: "" });
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    console.log('[ADMIN] load() started');
    setLoading(true);
    try {
      console.log('[ADMIN] Fetching tickets (simple query)...');
      const t = await db.from("weigh_tickets").select("*");
      console.log('[ADMIN] ALL tickets fetched:', t.data?.length);
      console.log('[ADMIN] Tickets data:', t);
      
      // Filter in JavaScript
      const pending = t.data ? t.data.filter(ticket => ticket.status === 'pending_invoice') : [];
      console.log('[ADMIN] Pending tickets:', pending.length);
      console.log('[ADMIN] Pending tickets:', pending.length);
      
      // Sort in JavaScript
      pending.sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime());
      
      // Update with filtered data
      const tFiltered = { data: pending, error: t.error };
      
      console.log('[ADMIN] Fetching pricing...');
      const p = await db.from("client_pricing").select("client_id,material_id,type,price_per_ton");
      console.log('[ADMIN] Pricing fetched:', p.data?.length);
      
      console.log('[ADMIN] Fetching settings...');
      const s = await db.from("settings").select("backoffice_email").eq("id", 1).single();
      console.log('[ADMIN] Settings fetched');
      
      console.log('[ADMIN] Fetching clients...');
      const clients = await db.from("clients").select("*");
      console.log('[ADMIN] Clients fetched:', clients.data?.length);
      
      console.log('[ADMIN] Fetching materials...');
      const materials = await db.from("materials").select("*");
      console.log('[ADMIN] Materials fetched:', materials.data?.length);
      
      console.log('[ADMIN] Fetching ticket_materials...');
      const ticketMats = await db.from("ticket_materials").select("*");
      console.log('[ADMIN] Ticket materials fetched:', ticketMats.data?.length);
      
      console.log('[ADMIN] Data fetched:', {
        tickets: tFiltered.data?.length,
        pricing: p.data?.length,
        settings: s.data,
        clients: clients.data?.length,
        materials: materials.data?.length,
        ticketMats: ticketMats.data?.length
      });
      
      // Build lookup maps
      const clientsMap = new Map((clients.data || []).map(c => [c.id, c]));
      const materialsMap = new Map((materials.data || []).map(m => [m.id, m]));
      
      console.log('[ADMIN] Building enriched tickets...');
      // Enrich tickets with relations
      if (tFiltered.data) {
        const enriched = tFiltered.data.map(ticket => ({
          ...ticket,
          clients: clientsMap.get(ticket.client_id),
          ticket_materials: (ticketMats.data || [])
            .filter(tm => tm.ticket_id === ticket.id)
            .map(tm => ({
              ...tm,
              materials: materialsMap.get(tm.material_id),
            })),
        }));
        console.log('[ADMIN] Enriched tickets:', enriched.length);
        setTickets(enriched);
      }
      
      if (p.data) {
        const map: Record<string, number> = {};
        p.data.forEach(r => { map[`${r.client_id}|${r.material_id}|${r.type}`] = Number(r.price_per_ton); });
        setPricing(map);
        console.log('[ADMIN] Pricing map built with', Object.keys(map).length, 'entries');
      }
      if (s.data) {
        setSettings(s.data);
        console.log('[ADMIN] Settings loaded');
      }
      console.log('[ADMIN] All data processed successfully');
    } catch (err) {
      console.error('[ADMIN] Failed to load data:', err);
    }
    console.log('[ADMIN] Setting loading to false');
    setLoading(false);
    console.log('[ADMIN] load() completed');
  };

  useEffect(() => { load(); }, []);

  const priceOf = (ticket: any, materialId: string) => {
    const k = `o:${ticket.id}:${materialId}`;
    if (overrides[k] !== undefined) return parseFloat(overrides[k]) || 0;
    return pricing[`${ticket.client_id}|${materialId}|${ticket.type}`] ?? 0;
  };

  const computeLines = (ticket: any) =>
    ticket.ticket_materials.map((m: any) => {
      const price = priceOf(ticket, m.materials.id);
      const tonnage = Number(m.tonnage);
      return {
        material_id: m.materials.id,
        material_name: m.materials.name,
        tonnage, price_per_ton: price,
        amount: +(tonnage * price).toFixed(2),
      };
    });

  const totalOf = (ticket: any) =>
    computeLines(ticket).reduce((s: number, l: any) => s + l.amount, 0);

  const validate = async (ticket: any) => {
    const lines = computeLines(ticket);
    const total = +lines.reduce((s: number, l: any) => s + l.amount, 0).toFixed(2);
    if (lines.some((l: any) => l.price_per_ton <= 0)) {
      if (!confirm("Certaines lignes ont un prix à 0. Continuer ?")) return;
    }
    setBusy(ticket.id);
    try {
      const res = await createInvoice({
        ticket_id: ticket.id,
        lines,
        total_ht: total,
        sent_to: ticket.recipient_email,
      });
      toast.success(`Facture ${(res as any).invoice.number} créée`);
      await load();
    } catch (err: any) {
      toast.error(err.message || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Factures en attente</h1>
        <p className="text-sm text-muted-foreground">
          {tickets.length} bon{tickets.length > 1 ? "s" : ""} de pesée à facturer
        </p>
      </div>

      {!settings.backoffice_email && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-3 text-sm text-amber-900">
            ⚠️ Email back-office non configuré dans les Paramètres : seul le client recevra la facture.
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : tickets.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <FileText className="mx-auto h-8 w-8 mb-2 opacity-50" />
          Aucun ticket en attente
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {tickets.map(t => {
            const lines = computeLines(t);
            const total = totalOf(t);
            const isExp = expanded[t.id];
            return (
              <Card key={t.id}>
                <CardHeader className="cursor-pointer" onClick={() => setExpanded(s => ({ ...s, [t.id]: !s[t.id] }))}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      {isExp ? <ChevronDown className="h-4 w-4 mt-1" /> : <ChevronRight className="h-4 w-4 mt-1" />}
                      <div>
                        <CardTitle className="text-base">{t.clients?.name} — {t.site_name}</CardTitle>
                        <CardDescription className="mt-1">
                          {fmtDateTime(t.occurred_at)} · {t.truck_plate} ·{" "}
                          <Badge variant={t.type === "depot" ? "default" : "secondary"}>{t.type === "depot" ? "Dépôt" : "Reprise"}</Badge>
                        </CardDescription>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold">{eur(total)}</div>
                      <div className="text-xs text-muted-foreground">HT</div>
                    </div>
                  </div>
                </CardHeader>
                {isExp && (
                  <CardContent className="space-y-4">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Matière</TableHead>
                          <TableHead className="text-right">Tonnage</TableHead>
                          <TableHead className="w-32">Prix /t (€)</TableHead>
                          <TableHead className="text-right">Montant</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lines.map((l: any) => (
                          <TableRow key={l.material_id}>
                            <TableCell>{l.material_name}</TableCell>
                            <TableCell className="text-right">{l.tonnage.toFixed(3)} t</TableCell>
                            <TableCell>
                              <Input
                                type="number" step="0.01" min="0"
                                value={overrides[`o:${t.id}:${l.material_id}`] ?? l.price_per_ton}
                                onChange={(e) => setOverrides(o => ({ ...o, [`o:${t.id}:${l.material_id}`]: e.target.value }))}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium">{eur(l.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="flex items-center justify-between gap-3 pt-2 border-t">
                      <div className="text-xs text-muted-foreground">
                        Sera envoyée à : <span className="font-medium">{t.recipient_email}</span>
                        {settings.backoffice_email && <> + <span className="font-medium">{settings.backoffice_email}</span></>}
                      </div>
                      <Button onClick={() => validate(t)} disabled={busy === t.id}>
                        {busy === t.id ? "…" : "Valider la facture"}
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
